// 004 (FR-004, FR-005, FR-043; US2-2): Edit details. Email is read-only; the name is required; the
// mobile number is optional, normalised, and checked before sending. Only changed properties are
// sent, and clearing a saved number sends `mobile: null` (decision 2026-10-06). One save at a time.
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";

import type { Api } from "@/api/types";
import { isAppError } from "@/api/errors";
import { appNotices } from "@/shell/notice/store";
import { ScreenScaffold } from "@/shell/ScreenScaffold";
import { StateView } from "@/shell/StateView";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { PROFILE_COPY, profileErrorMessage } from "./copy";
import { Card, OfflineNote } from "./ui";
import { useParentData } from "./useParentData";
import { normaliseMobile, validateName } from "./validation";

/** "+919848022338" → "98480 22338" for the field. */
const mobileField = (m: string | null) =>
  m ? m.replace(/^\+91(\d{5})(\d{5})$/, "$1 $2") : "";

function Form({ profile }: { profile: Api["Profile"] }) {
  const router = useRouter();
  const { online } = useNetworkStatus();
  const { actions } = useParentData();
  const [name, setName] = useState(profile.name);
  const [mobile, setMobile] = useState(mobileField(profile.mobile));
  const [errors, setErrors] = useState<{ name?: string; mobile?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function back() {
    if (router.canGoBack()) router.back();
    else router.replace("/profile");
  }

  async function save() {
    if (pending || !online) return;
    const nameError = validateName(name);
    const m = normaliseMobile(mobile);
    const next = {
      name: nameError ?? undefined,
      mobile: m.ok ? undefined : m.error,
    };
    setErrors(next);
    setFormError(null);
    if (nameError || !m.ok) return;
    const body: Api["ProfileUpdate"] = {};
    if (name.trim() !== profile.name) body.name = name.trim();
    const saved = profile.mobile ? profile.mobile.replace(/^\+91/, "") : null;
    if (m.value !== saved) body.mobile = m.value;
    if (Object.keys(body).length === 0) {
      back();
      return;
    }
    setPending(true);
    try {
      await actions.updateProfile(body);
      appNotices.post("Your details have been saved");
      back();
    } catch (e) {
      if (!isAppError(e)) throw e;
      const msg = profileErrorMessage(e, { action: "edit" });
      setErrors({ name: msg.fields?.name, mobile: msg.fields?.mobile });
      setFormError(msg.form ?? null);
    } finally {
      setPending(false);
    }
  }

  return (
    <ScreenScaffold
      title={PROFILE_COPY.editDetails}
      testID="edit-details-screen"
      narrow
      fallbackHref="/profile"
      cancel={
        <Button variant="outline" onPress={back}>
          {PROFILE_COPY.cancel}
        </Button>
      }
      action={
        <Button
          onPress={() => void save()}
          pending={pending}
          disabled={!online}
        >
          {PROFILE_COPY.save}
        </Button>
      }
    >
      <Card testID="edit-details">
        <TextField
          label={PROFILE_COPY.email}
          value={profile.email}
          onChangeText={() => {}}
          kind="email"
          editable={false}
        />
        <Text tone="muted" size="sm">
          {PROFILE_COPY.emailNote}
        </Text>
        <TextField
          label={PROFILE_COPY.name}
          value={name}
          onChangeText={setName}
          kind="name"
          error={errors.name}
        />
        <TextField
          label={PROFILE_COPY.mobile}
          value={mobile}
          onChangeText={setMobile}
          kind="tel"
          error={errors.mobile}
        />
        {formError ? (
          <InlineMessage tone="error">{formError}</InlineMessage>
        ) : null}
        <OfflineNote online={online} />
      </Card>
    </ScreenScaffold>
  );
}

export function EditDetailsScreen() {
  const { profile, actions } = useParentData();
  // Opened directly (web reload): load the profile once.
  const missing = profile.data === null;
  useEffect(() => {
    if (missing) void actions.refresh();
  }, [missing, actions]);
  if (profile.data) return <Form profile={profile.data} />;
  return (
    <ScreenScaffold
      title={PROFILE_COPY.editDetails}
      testID="edit-details-screen"
      narrow
      fallbackHref="/profile"
    >
      {profile.error ? (
        <StateView
          status="error"
          error={profile.error}
          onRetry={() => void actions.refresh()}
        />
      ) : (
        <StateView status="loading" />
      )}
    </ScreenScaffold>
  );
}
